import {useCallback} from 'react';
import {Platform} from 'react-native';
import {useFocusEffect} from '@react-navigation/native';

import {useOptionalScrollCollapseContext} from '../context/ScrollCollapseContext';

import {useEdgeToEdgeBottomInset} from './useEdgeToEdgeBottomInset';
import {useWebScreenDimensions} from './useWebScreenDimensions';

type Options = {
  /** Skip resetting scroll-collapse state on focus. Set true when the screen
   *  already uses useScrollCollapseHandler (which has its own focus sync). */
  skipFocusReset?: boolean;
};

/**
 * Returns the bottom padding needed to compensate for the absolutely-positioned
 * animated tab bar on web mobile. Returns 0 on native (tab bar is in normal
 * layout flow), web desktop (no bottom tabs), or outside the
 * ScrollCollapseProvider (modals, checkout flows).
 *
 * Also resets scroll-collapse state on focus so that the header and tab bar
 * start visible when navigating to a screen that doesn't have its own
 * scroll-collapse handler (e.g. Product Detail, Login, Checkout screens).
 * Pass `{skipFocusReset: true}` for screens that already use useScrollCollapseHandler.
 */
export function useTabBarBottomPadding(options?: Options): number {
  const ctx = useOptionalScrollCollapseContext();
  const {isWebDesktopScreen} = useWebScreenDimensions();
  const bottomInset = useEdgeToEdgeBottomInset();
  const skipReset = options?.skipFocusReset ?? false;

  // Reset scroll-collapse state when this screen gains focus.
  // Screens with useScrollCollapseHandler have their own useScrollFocusSync;
  // this covers screens that don't participate in scroll-collapse but still
  // need the header/tab bar visible.
  useFocusEffect(
    useCallback(() => {
      if (skipReset || !ctx || Platform.OS !== 'web') return;
      ctx.scrollY.set(0);
      ctx.prevScrollY.set(0);
      ctx.accumulatedUp.set(ctx.TAB_BAR_HEIGHT);
      ctx.accumulatedDown.set(0);
    }, [ctx, skipReset]),
  );

  // Native: tab bar is in normal layout flow (not absolute), so no padding needed.
  // Web desktop: no bottom tabs at all.
  // Outside ScrollCollapseProvider: modals/checkout flows, no tab bar.
  // Web mobile only: AnimatedTabBar is position:absolute and overlays content.
  if (!ctx || isWebDesktopScreen || Platform.OS !== 'web') return 0;
  return ctx.TAB_BAR_HEIGHT + bottomInset;
}
